import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentProductPackage } from '../duck/selectors/products';
import { State } from '../../../model/State';
import { fetchCurrentProductPackage } from '../duck/actions/product';
import { FormattedMessage } from 'react-intl';
import {
  bambooKeys,
  bitbucketBtfKeys,
  confluenceKeys,
  crowdKeys,
  fisheyeKeys,
  jiraPreRenaissanceKeys,
  jiraRenaissanceKeys,
} from '../../../util/mac/helpers';

const ServerIdListedInstruction = ({ productInfo }) => (
  <li>
    <FormattedMessage
      id="mac.license.evaluation.sid.server.listed.instruction"
      defaultMessage="Your Server ID is listed in the {productInfo} section"
      values={{
        productInfo: <strong>{productInfo}</strong>,
      }}
    />
  </li>
);

const FindServerIdInstruction = ({ serverIdLink }) => (
  <p>
    <a href={serverIdLink}>
      <FormattedMessage
        id="mac.license.evaluation.sid.cannot.find.question"
        defaultMessage="Can't find server ID?"
      />
    </a>
  </p>
);

const LoginInstruction = ({ productName }) => (
  <li>
    <FormattedMessage
      id="mac.license.evaluation.sid.login.instructions"
      defaultMessage="Log in as a {productName} administrator"
      values={{
        productName,
      }}
    />
  </li>
);

const SettingsInstruction = ({ settingsName }) => (
  <li>
    <FormattedMessage
      id="mac.license.evaluation.sid.go-to-settings.instruction"
      defaultMessage="Click on the Settings icon and choose {settingsName}"
      values={{
        settingsName: <strong>{settingsName}</strong>,
      }}
    />
  </li>
);

const DetailsLinkInstruction = ({ details, underWhom = '' }) => (
  <li>
    <FormattedMessage
      id="mac.license.evaluation.sid.details.link.instruction"
      defaultMessage="Click on the {details} link {underWhom}"
      values={{
        details: <strong>{details}</strong>,
        underWhom: underWhom ? (
          <>
            (under &apos;<em>{underWhom}</em>&apos;)
          </>
        ) : null,
      }}
    />
  </li>
);

const JiraRenaissanceServerIdInstructionsView = ({ selectedInstanceOption }) => {
  return (
    <>
      {selectedInstanceOption === 'upAndRunning' ? (
        <>
          <ol>
            <LoginInstruction productName="JIRA" />
            <SettingsInstruction settingsName="System" />
            <DetailsLinkInstruction details="System info" />
            <ServerIdListedInstruction productInfo="JIRA info" />
          </ol>
          <FindServerIdInstruction serverIdLink="https://confluence.atlassian.com/display/AdminJIRAServer070/Finding+your+Server+ID" />
        </>
      ) : (
        <>
          <ol>
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.locate.instruction"
                defaultMessage="You can locate your Server ID on the {licenseCta} screen in the JIRA setup wizard."
                values={{
                  licenseCta: <strong>Specify your license key</strong>,
                }}
              />
            </li>
          </ol>
          <FindServerIdInstruction serverIdLink="https://confluence.atlassian.com/display/AdminJIRAServer070/Finding+your+Server+ID" />
        </>
      )}
    </>
  );
};

const JiraServerIdInstructionsView = ({ selectedInstanceOption }) => {
  return (
    <>
      {selectedInstanceOption === 'upAndRunning' ? (
        <>
          <ol>
            <LoginInstruction productName="JIRA" />
            <SettingsInstruction settingsName="System" />
            <DetailsLinkInstruction details="License" />
            <ServerIdListedInstruction productInfo="Server ID" />
          </ol>
          <FindServerIdInstruction serverIdLink="https://confluence.atlassian.com/display/JIRA/Finding+your+Server+ID" />
        </>
      ) : (
        <>
          <ol>
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.locate.sid.under.section"
                defaultMessage="You can locate your Server ID under {sectionName} section of the {productName}"
                values={{
                  sectionName: 'Step 2 (<strong>Application Properties</strong>)',
                  productName: 'JIRA Setup Wizard',
                }}
              />
            </li>
          </ol>
          <FindServerIdInstruction serverIdLink="https://confluence.atlassian.com/display/JIRA/Finding+your+Server+ID" />
        </>
      )}
    </>
  );
};

const ConfluenceServerIdInstructionsView = ({ selectedInstanceOption }) => {
  return (
    <>
      {selectedInstanceOption === 'upAndRunning' ? (
        <>
          <ol>
            <LoginInstruction productName="Confluence" />
            <SettingsInstruction settingsName="General configuration" />
            <DetailsLinkInstruction details="License Details" underWhom="Administration" />
            <ServerIdListedInstruction productInfo="Server ID" />
          </ol>
        </>
      ) : (
        <>
          <ol>
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.locate.confluence.instruction"
                defaultMessage="You can locate your Server ID under {sectionName} section of the Confluence Setup Wizard"
                values={{
                  sectionName: <strong>Enter License</strong>,
                }}
              />
            </li>
          </ol>
        </>
      )}
    </>
  );
};

const BitbucketServerIdInstructionsView = ({ selectedInstanceOption }) => {
  return (
    <>
      {selectedInstanceOption === 'upAndRunning' ? (
        <>
          <ol>
            <LoginInstruction productName="Bitbucket" />
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.details.settings.icon.instruction"
                defaultMessage="Click on the Settings icon"
              />
            </li>
            <DetailsLinkInstruction details="Licensing" underWhom="Settings" />
            <ServerIdListedInstruction productInfo="Server ID" />
          </ol>
        </>
      ) : (
        <>
          <ol>
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.locate.bitbucket.instruction"
                defaultMessage="You can locate your Server ID on the setup page of the Bitbucket Installer"
              />
            </li>
          </ol>
        </>
      )}
    </>
  );
};

const BambooServerIdInstructionsView = ({ selectedInstanceOption }) => {
  return (
    <>
      {selectedInstanceOption === 'upAndRunning' ? (
        <>
          <ol>
            <LoginInstruction productName="Bamboo" />
            <SettingsInstruction settingsName="Overview" />
            <DetailsLinkInstruction details="License Details" underWhom="System" />
            <ServerIdListedInstruction productInfo="Server ID" />
          </ol>
        </>
      ) : (
        <>
          <ol>
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.locate.bamboo.instruction"
                defaultMessage="You can locate your Server ID in the first page of the Bamboo Setup Wizard"
              />
            </li>
          </ol>
        </>
      )}
    </>
  );
};

const CrowdServerIdInstructionsView = ({ selectedInstanceOption }) => {
  return (
    <>
      {selectedInstanceOption === 'upAndRunning' ? (
        <>
          <ol>
            <LoginInstruction productName="Crowd" />
            <SettingsInstruction settingsName="Licensing" />
            <ServerIdListedInstruction productInfo="License Server ID" />
          </ol>
        </>
      ) : (
        <>
          <ol>
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.locate.crowd.instruction"
                defaultMessage="You can locate your Server ID in the first page of the Crowd Setup Wizard"
              />
            </li>
          </ol>
        </>
      )}
    </>
  );
};

const CrucibleServerIdInstructionsView = ({ selectedInstanceOption }) => {
  return (
    <>
      {selectedInstanceOption === 'upAndRunning' ? (
        <>
          <ol>
            <LoginInstruction productName="Crucible" />
            <SettingsInstruction settingsName="Administration" />
            <DetailsLinkInstruction details="System Info" underWhom="System Settings" />
            <ServerIdListedInstruction productInfo="Server ID" />
          </ol>
        </>
      ) : (
        <>
          <ol>
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.locate.sid.under.section"
                defaultMessage="You can locate your Server ID under {sectionName} section of the {productName}"
                values={{
                  sectionName: <strong>License</strong>,
                  productName: 'Crucible Setup Wizard',
                }}
              />
            </li>
          </ol>
        </>
      )}
    </>
  );
};

const FishEyeServerIdInstructionsView = ({ selectedInstanceOption }) => {
  return (
    <>
      {selectedInstanceOption === 'upAndRunning' ? (
        <>
          <ol>
            <LoginInstruction productName="FishEye" />
            <SettingsInstruction settingsName="Administration" />
            <DetailsLinkInstruction details="System Info" underWhom="System Settings" />
            <ServerIdListedInstruction productInfo="Server ID" />
          </ol>
        </>
      ) : (
        <>
          <ol>
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.locate.sid.under.section"
                defaultMessage="You can locate your Server ID under {sectionName} section of the {productName}"
                values={{
                  sectionName: <strong>License</strong>,
                  productName: 'FishEye Setup Wizard',
                }}
              />
            </li>
          </ol>
        </>
      )}
    </>
  );
};

const HipchatServerInstructionView = ({ selectedInstanceOption }) => {
  return (
    <>
      {selectedInstanceOption === 'upAndRunning' ? (
        <>
          <ol>
            <LoginInstruction productName="HipChat" />
            <DetailsLinkInstruction details="Server admin" />
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.details.license.tab.instruction"
                defaultMessage="Click on the License tab"
              />
            </li>
            <ServerIdListedInstruction productInfo="Server ID" />
          </ol>
        </>
      ) : (
        <>
          <ol>
            <li>
              <FormattedMessage
                id="mac.license.evaluation.sid.locate.sid.under.section"
                defaultMessage="You can locate your Server ID under {sectionName} section of the {productName}"
                values={{
                  sectionName: 'Step 3 (<strong>License</strong>)',
                  productName: 'HipChat Setup Wizard',
                }}
              />
            </li>
          </ol>
        </>
      )}
    </>
  );
};

type SidInstructionsProps = {
  selectedInstanceOption: string;
  productKey: string;
};

const SidInstructions = ({ selectedInstanceOption, productKey }: SidInstructionsProps) => {
  const dispatch = useDispatch();

  const selectedProductPackageKey = useSelector(
    (state) => selectCurrentProductPackage(state as State, productKey)?.details?.key,
  );

  useEffect(() => {
    dispatch(fetchCurrentProductPackage(productKey));
  }, [productKey]);

  if (!selectedProductPackageKey) {
    return null;
  }

  const productKeyToComponentMap: { [key: string]: React.JSX.Element | null } = {
    jiraRenaissance: (
      <JiraRenaissanceServerIdInstructionsView selectedInstanceOption={selectedInstanceOption} />
    ),
    jiraPreRenaissance: (
      <JiraServerIdInstructionsView selectedInstanceOption={selectedInstanceOption} />
    ),
    confluence: (
      <ConfluenceServerIdInstructionsView selectedInstanceOption={selectedInstanceOption} />
    ),
    bitbucketBTF: (
      <BitbucketServerIdInstructionsView selectedInstanceOption={selectedInstanceOption} />
    ),
    bamboo: <BambooServerIdInstructionsView selectedInstanceOption={selectedInstanceOption} />,
    crowd: <CrowdServerIdInstructionsView selectedInstanceOption={selectedInstanceOption} />,
    crucible: <CrucibleServerIdInstructionsView selectedInstanceOption={selectedInstanceOption} />,
    fishEye: <FishEyeServerIdInstructionsView selectedInstanceOption={selectedInstanceOption} />,
    hipchatServer: <HipchatServerInstructionView selectedInstanceOption={selectedInstanceOption} />,
  };

  let instructionsView;
  if (jiraRenaissanceKeys.includes(selectedProductPackageKey)) {
    instructionsView = productKeyToComponentMap.jiraRenaissance;
  } else if (jiraPreRenaissanceKeys.includes(selectedProductPackageKey)) {
    instructionsView = productKeyToComponentMap.jiraPreRenaissance;
  } else if (confluenceKeys.includes(selectedProductPackageKey)) {
    instructionsView = productKeyToComponentMap.confluence;
  } else if (bitbucketBtfKeys.includes(selectedProductPackageKey)) {
    instructionsView = productKeyToComponentMap.bitbucketBTF;
  } else if (bambooKeys.includes(selectedProductPackageKey)) {
    instructionsView = productKeyToComponentMap.bamboo;
  } else if (crowdKeys.includes(selectedProductPackageKey)) {
    instructionsView = productKeyToComponentMap.crowd;
  } else if (selectedProductPackageKey === 'crucible') {
    instructionsView = productKeyToComponentMap.crucible;
  } else if (fisheyeKeys.includes(selectedProductPackageKey)) {
    instructionsView = productKeyToComponentMap.fishEye;
  } else if (selectedProductPackageKey === 'com.atlassian.hipchat.server') {
    instructionsView = productKeyToComponentMap.hipchatServer;
  }
  return !instructionsView ? null : (
    <div>
      <p>To get your Server ID:</p>
      {instructionsView}
    </div>
  );
};

export default SidInstructions;
